import React, { useMemo, useState } from "react";
import {
  AlertCircle, CalendarCheck, CheckCircle2, Loader2, Plus, RefreshCw, Search,
  Settings2, ThumbsDown, ThumbsUp, Wallet,
} from "lucide-react";
import HrModal from "../components/HrModal";
import {
  useHrApproveLeaveMutation,
  useHrCreateLeaveRequestMutation,
  useHrCreateLeaveTypeMutation,
  useHrGetLeaveBalancesQuery,
  useHrGetLeaveRequestsQuery,
  useHrGetLeaveTypesQuery,
  useHrGetStaffQuery,
  useHrRejectLeaveMutation,
  useHrSetLeaveBalanceMutation,
  useHrUpdateLeaveTypeMutation,
} from "../api/hrApi";
import { errMsg, fmtDate, leaveColor, staffName, todayIso } from "../utils/hr";

type Tab = "requests" | "types" | "balances";

const REQUEST_TABS = ["all", "pending", "approved", "rejected"] as const;

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

/** Leave requests, the leave types they draw from, and per-staff entitlements. */
export default function HrLeavePage() {
  const [tab, setTab] = useState<Tab>("requests");

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
            <CalendarCheck className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Leave</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Approving a request writes those days straight into the attendance register.
            </p>
          </div>
        </div>

        {/* Section tabs */}
        <div className="mt-5 flex flex-wrap gap-2">
          {([
            { key: "requests", label: "Requests", icon: <CalendarCheck className="w-3.5 h-3.5" /> },
            { key: "types", label: "Leave types", icon: <Settings2 className="w-3.5 h-3.5" /> },
            { key: "balances", label: "Balances", icon: <Wallet className="w-3.5 h-3.5" /> },
          ] as { key: Tab; label: string; icon: React.ReactNode }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border rounded-lg transition-colors ${
                tab === t.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "requests" && <RequestsTab />}
          {tab === "types" && <TypesTab />}
          {tab === "balances" && <BalancesTab />}
        </div>
      </div>
    </div>
  );
}

/* ── Requests ─────────────────────────────────────────────────────────────── */

function RequestsTab() {
  const [status, setStatus] = useState<(typeof REQUEST_TABS)[number]>("pending");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [deciding, setDeciding] = useState<{ row: any; action: "approve" | "reject" } | null>(null);

  const { data, isLoading, isFetching, refetch } = useHrGetLeaveRequestsQuery({});
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    m.set("all", rows.length);
    for (const r of rows) {
      const k = String(r.status ?? "").toLowerCase();
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((r: any) => {
      if (status !== "all" && String(r.status ?? "").toLowerCase() !== status) return false;
      if (!nq) return true;
      return [staffName(r.admin), r.leave_type?.name, r.leaveType?.name, r.reason]
        .filter(Boolean).join(" ").toLowerCase().includes(nq);
    });
  }, [rows, status, q]);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {REQUEST_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setStatus(t)}
              className={`px-3 py-1.5 text-sm border rounded-lg capitalize transition-colors ${
                status === t ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t}
              <span className="ml-2 text-xs opacity-70">{counts.get(t) || 0}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> New request
          </button>
          <button onClick={() => refetch()} disabled={isFetching} className="p-2 rounded-lg border hover:bg-gray-50" aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by staff, leave type or reason…"
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
        />
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
          {rows.length === 0 ? "No leave requests yet." : "No requests match your filters."}
        </div>
      ) : (
        <div className="mt-5 border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                <th className="px-4 py-2 font-medium">Staff</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Period</th>
                <th className="px-4 py-2 font-medium text-right">Days</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Decided by</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r: any) => {
                const type = r.leave_type ?? r.leaveType;
                return (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{staffName(r.admin)}</div>
                      {r.reason && (
                        <div className="text-xs text-gray-400 truncate max-w-[220px]" title={r.reason}>{r.reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{type?.name ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-800">{r.days ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${leaveColor(r.status)}`}>
                        {r.status ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {r.decided_by?.name ?? r.decidedBy?.name ?? "—"}
                      {r.decision_note && (
                        <div className="text-gray-400 truncate max-w-[160px]" title={r.decision_note}>{r.decision_note}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {String(r.status).toLowerCase() === "pending" ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setDeciding({ row: r, action: "approve" })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-200 text-green-700 text-xs font-semibold hover:bg-green-50"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => setDeciding({ row: r, action: "reject" })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 text-xs font-semibold hover:bg-rose-50"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-right text-xs text-gray-300">Decided</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewRequestModal onClose={() => setShowNew(false)} />}
      {deciding && (
        <DecisionModal
          row={deciding.row}
          action={deciding.action}
          onClose={() => setDeciding(null)}
        />
      )}
    </>
  );
}

function NewRequestModal({ onClose }: { onClose: () => void }) {
  const { data: staffData } = useHrGetStaffQuery({});
  const { data: types = [] } = useHrGetLeaveTypesQuery();
  const [create, { isLoading }] = useHrCreateLeaveRequestMutation();

  const [form, setForm] = useState({
    admin_id: "", leave_type_id: "", start_date: todayIso(), end_date: todayIso(), reason: "",
  });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create({
        admin_id: form.admin_id,
        leave_type_id: form.leave_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason.trim() || undefined,
      }).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <HrModal title="New leave request" icon={<CalendarCheck className="w-4 h-4 text-indigo-600" />} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Days are counted as working days — weekends are excluded automatically.
        </p>

        <div>
          <label className={labelCls}>Staff *</label>
          <select required value={form.admin_id} onChange={(e) => setForm((f) => ({ ...f, admin_id: e.target.value }))} className={inputCls}>
            <option value="">Select staff…</option>
            {(staffData?.rows ?? []).map((s: any) => (
              <option key={s.id} value={s.id}>{staffName(s)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Leave type *</label>
          <select required value={form.leave_type_id} onChange={(e) => setForm((f) => ({ ...f, leave_type_id: e.target.value }))} className={inputCls}>
            <option value="">Select type…</option>
            {(types as any[]).map((t: any) => (
              <option key={t.id} value={t.id}>{t.name}{t.default_days ? ` (${t.default_days} days)` : ""}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>From *</label>
            <input type="date" required value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>To *</label>
            <input type="date" required value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Reason</label>
          <textarea rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className={inputCls} />
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />} Submit
          </button>
        </div>
      </form>
    </HrModal>
  );
}

function DecisionModal({
  row, action, onClose,
}: { row: any; action: "approve" | "reject"; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [approve, { isLoading: approving }] = useHrApproveLeaveMutation();
  const [reject, { isLoading: rejecting }] = useHrRejectLeaveMutation();

  const isReject = action === "reject";
  const busy = approving || rejecting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isReject) await reject({ id: row.id, note: note.trim() }).unwrap();
      else await approve({ id: row.id, note: note.trim() || undefined }).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <HrModal
      title={isReject ? "Reject leave request" : "Approve leave request"}
      icon={isReject ? <ThumbsDown className="w-4 h-4 text-rose-600" /> : <ThumbsUp className="w-4 h-4 text-green-600" />}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="px-4 py-3 rounded-xl bg-gray-50 border text-sm">
          <div className="font-medium text-gray-900">{staffName(row.admin)}</div>
          <div className="text-gray-600 mt-0.5">
            {(row.leave_type ?? row.leaveType)?.name ?? "Leave"} · {fmtDate(row.start_date)} – {fmtDate(row.end_date)} ·{" "}
            <span className="font-medium">{row.days} working day{row.days !== 1 ? "s" : ""}</span>
          </div>
          {row.reason && <div className="text-xs text-gray-500 mt-1 italic">"{row.reason}"</div>}
        </div>

        {!isReject && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-sky-50 border border-sky-100 text-xs text-sky-800">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Approving marks each of these working days as leave in the attendance register and draws them from the
            staff member's balance.
          </div>
        )}

        <div>
          <label className={labelCls}>
            {isReject ? "Reason for rejection *" : "Note (optional)"}
          </label>
          <textarea
            rows={3}
            required={isReject}
            minLength={isReject ? 3 : undefined}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={isReject ? "Tell the staff member why…" : ""}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className={`flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2 ${
              isReject ? "bg-rose-600 hover:bg-rose-700" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isReject ? "Reject" : "Approve"}
          </button>
        </div>
      </form>
    </HrModal>
  );
}

/* ── Leave types ──────────────────────────────────────────────────────────── */

function TypesTab() {
  const { data: types = [], isLoading } = useHrGetLeaveTypesQuery({ all: true });
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          The categories staff can take leave under, and the default entitlement each carries.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 shrink-0"
        >
          <Plus className="w-4 h-4" /> New type
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (types as any[]).length === 0 ? (
        <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
          No leave types yet — create one before requests can be raised.
        </div>
      ) : (
        <div className="mt-5 border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Code</th>
                <th className="px-4 py-2 font-medium text-right">Default days</th>
                <th className="px-4 py-2 font-medium">Paid</th>
                <th className="px-4 py-2 font-medium">Active</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(types as any[]).map((t: any) => (
                <tr key={t.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-gray-600">{t.code}</span></td>
                  <td className="px-4 py-3 text-right text-gray-700">{t.default_days ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{t.paid ? "Paid" : "Unpaid"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${
                      t.active ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-600 border-gray-200"
                    }`}>
                      {t.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button onClick={() => setEditing(t)} className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <TypeModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
        />
      )}
    </>
  );
}

function TypeModal({ initial, onClose }: { initial?: any | null; onClose: () => void }) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    code: initial?.code ?? "",
    default_days: initial?.default_days ?? "",
    paid: initial?.paid ?? true,
    active: initial?.active ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const [create, { isLoading: creating }] = useHrCreateLeaveTypeMutation();
  const [update, { isLoading: updating }] = useHrUpdateLeaveTypeMutation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = {
      name: form.name.trim(),
      code: form.code.trim(),
      default_days: form.default_days === "" ? null : Number(form.default_days),
      paid: !!form.paid,
      active: !!form.active,
    };
    try {
      if (initial) await update({ id: initial.id, body }).unwrap();
      else await create(body).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <HrModal
      title={initial ? "Edit leave type" : "New leave type"}
      icon={<Settings2 className="w-4 h-4 text-indigo-600" />}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Name *</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Annual Leave" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Code *</label>
            <input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="e.g. ANN" className={inputCls} />
          </div>
        </div>

        <div>
          <label className={labelCls}>Default days per year</label>
          <input
            type="number" min={0} max={365}
            value={form.default_days}
            onChange={(e) => setForm((f) => ({ ...f, default_days: e.target.value }))}
            className={inputCls}
          />
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm((f) => ({ ...f, paid: e.target.checked }))} className="h-4 w-4" />
            Paid leave
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4" />
            Active
          </label>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="submit"
            disabled={creating || updating}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {(creating || updating) && <Loader2 className="w-4 h-4 animate-spin" />}
            {initial ? "Save changes" : "Create type"}
          </button>
        </div>
      </form>
    </HrModal>
  );
}

/* ── Balances ─────────────────────────────────────────────────────────────── */

function BalancesTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [q, setQ] = useState("");
  const [allocating, setAllocating] = useState(false);

  const { data: balances = [], isLoading } = useHrGetLeaveBalancesQuery({ year });

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return balances;
    return (balances as any[]).filter((b: any) =>
      `${b.staff ?? ""} ${b.leave_type ?? ""}`.toLowerCase().includes(nq)
    );
  }, [balances, q]);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
        <div className="flex gap-3">
          <div>
            <label className={labelCls}>Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
            >
              {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[12rem]">
            <label className={labelCls}>Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Staff or leave type…"
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>
        </div>
        <button
          onClick={() => setAllocating(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 shrink-0"
        >
          <Plus className="w-4 h-4" /> Allocate entitlement
        </button>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center text-gray-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-5 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
          No entitlements allocated for {year}.
        </div>
      ) : (
        <div className="mt-5 border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                <th className="px-4 py-2 font-medium">Staff</th>
                <th className="px-4 py-2 font-medium">Leave type</th>
                <th className="px-4 py-2 font-medium text-right">Entitled</th>
                <th className="px-4 py-2 font-medium text-right">Used</th>
                <th className="px-4 py-2 font-medium text-right">Remaining</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(filtered as any[]).map((b: any) => (
                <tr key={b.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-medium text-gray-900">{b.staff ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{b.leave_type ?? "—"}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{b.entitled_days}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{b.used_days}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${
                    Number(b.remaining_days) <= 0 ? "text-rose-600" : "text-gray-900"
                  }`}>
                    {b.remaining_days}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {allocating && <BalanceModal year={year} onClose={() => setAllocating(false)} />}
    </>
  );
}

function BalanceModal({ year, onClose }: { year: number; onClose: () => void }) {
  const { data: staffData } = useHrGetStaffQuery({});
  const { data: types = [] } = useHrGetLeaveTypesQuery();
  const [setBalance, { isLoading }] = useHrSetLeaveBalanceMutation();

  const [form, setForm] = useState({ admin_id: "", leave_type_id: "", year, entitled_days: "" });
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await setBalance({
        admin_id: form.admin_id,
        leave_type_id: form.leave_type_id,
        year: Number(form.year),
        entitled_days: Number(form.entitled_days),
      }).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <HrModal title="Allocate leave entitlement" icon={<Wallet className="w-4 h-4 text-indigo-600" />} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Setting an entitlement that already exists adjusts it — days already used are kept.
        </p>

        <div>
          <label className={labelCls}>Staff *</label>
          <select required value={form.admin_id} onChange={(e) => setForm((f) => ({ ...f, admin_id: e.target.value }))} className={inputCls}>
            <option value="">Select staff…</option>
            {(staffData?.rows ?? []).map((s: any) => (
              <option key={s.id} value={s.id}>{staffName(s)}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-1">
            <label className={labelCls}>Leave type *</label>
            <select required value={form.leave_type_id} onChange={(e) => setForm((f) => ({ ...f, leave_type_id: e.target.value }))} className={inputCls}>
              <option value="">Select…</option>
              {(types as any[]).map((t: any) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Year *</label>
            <input
              type="number" required min={2000} max={2100}
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Entitled days *</label>
            <input
              type="number" required min={0} max={365} step="0.5"
              value={form.entitled_days}
              onChange={(e) => setForm((f) => ({ ...f, entitled_days: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </HrModal>
  );
}
